/**
 * Knowledge Base - Comprehensive artifacts and chunks viewer
 * Displays all artifacts, their chunks, and document gap analysis
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { EmptyState } from '../UI/EmptyState';
import { 
  listArtifacts, 
  deleteArtifact, 
  reingestArtifact, 
  downloadArtifactWithToast,
  uploadArtifact,
  getArtifactChunks,
  searchArtifacts,
  getArtifactStats,
  getChunkCountsBatch,
} from '../../api/artifacts';
import { queryEntities, getEntityCountsBatch } from '../../api/entities';
import { getBM25Status, buildBM25Index } from '../../api/indices';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useToastStore } from '../../stores/toast';
import { listSources } from '../../api/sources';
import type { Artifact } from '../../types/api';
import { DocumentGapsPanel } from '../Analytics/DocumentGapsPanel';
import { FileTree } from './FileTree';
import { AdvancedArtifactSearch } from './AdvancedArtifactSearch';
import { RegistryFilterBar, DEFAULT_REGISTRY_FILTERS } from './RegistryFilterBar';
import { RegistryMetaBadges } from './RegistryMetaBadges';
import { RelatedRegistryArtifacts } from './RelatedRegistryArtifacts';
import { buildFileTree, TreeNode } from '../../utils/pathParser';
import {
  applyRegistryFilters,
  getRegistryFilterOptions,
  groupArtifactsByRegistryField,
  hasRegistryMeta,
  getProjectGroupDisplayLabel,
  type RegistryFilters,
  type RegistryGroupBy,
} from '../../utils/registryArtifactUtils';
import {
  getDisplayChunkCount,
  getDisplayEntityCount,
  getIngestStatusBadgeClass,
  getIngestStatusLabel,
  isArtifactStatsStale,
} from '../../utils/artifactDisplayUtils';
import { RegistryMetaDetailGrid } from './RegistryMetaDetailGrid';
import { 
  FileText, 
  Download, 
  Search, 
  Upload,
  Trash2,
  RefreshCw,
  Database,
  Eye,
  ChevronDown,
  ChevronRight,
  FileCheck,
  AlertCircle,
  BarChart3,
  Package,
  Layers,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Table,
  Zap,
  CheckCircle,
  Folder,
  FolderOpen,
  Network,
  List,
  FolderTree,
  Filter,
  X,
} from 'lucide-react';

export function KnowledgeBase() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterSourceId, setFilterSourceId] = useState<string | null>(null); // Source ID filter from URL
  const [registryFilters, setRegistryFilters] = useState<RegistryFilters>(DEFAULT_REGISTRY_FILTERS);
  const [groupBy, setGroupBy] = useState<RegistryGroupBy>('none');
  const [expandedArtifact, setExpandedArtifact] = useState<string | null>(null);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'tree' | 'gaps'>('list');
  const [sortBy, setSortBy] = useState<'size' | 'date' | 'chunks' | 'entities' | 'name' | 'status'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const [expandedTreePaths, setExpandedTreePaths] = useState<Set<string>>(new Set());
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]| null>(null);
  const [searchResultsTotal, setSearchResultsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(100);

  // Sort option: 'recent_upload' or 'recent_indexed'
  // Note: Backend currently sorts by created_at (upload date) for both options
  const [sortByDate, setSortByDate] = useState<'recent_upload' | 'recent_indexed'>('recent_upload');
  
  // Read source_id from URL params on mount (before sources query)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceIdParam = urlParams.get('source_id');
    if (sourceIdParam) {
      setFilterSourceId(sourceIdParam);
    }
  }, []); // Only run once on mount

  React.useEffect(() => {
    if (!showUpload) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUpload(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showUpload]);
  
  // Fetch sources for the source filter dropdown
  const { data: sources } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => listSources(projectId!),
    enabled: !!projectId,
  });
  
  // Update filterSource when sources load and we have a source_id from URL
  React.useEffect(() => {
    if (!sources || !Array.isArray(sources) || !filterSourceId) return;
    
    // Find source name from sources list and set filterSource
    const source = sources.find(s => s.id === filterSourceId);
    if (source) {
      setFilterSource(source.name || source.description || source.id);
    }
  }, [sources, filterSourceId]);

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0); // Reset to first page on search
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Determine if we should use search API or list API
  const hasActiveFilters = debouncedSearchTerm.trim() !== '' || filterType !== 'all' || filterSourceId !== null;
  
  // Use search API when filters are active, otherwise use list API
  const { data: artifactsData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['artifacts', projectId, debouncedSearchTerm, filterType, filterSourceId, currentPage],
    queryFn: async () => {
      if (hasActiveFilters) {
        // Use search API with filters
        const searchParams: any = {
          project_id: projectId!,
          limit: pageSize,
          offset: currentPage * pageSize,
        };
        
        if (debouncedSearchTerm.trim()) {
          searchParams.query = debouncedSearchTerm.trim();
        }
        
        if (filterType !== 'all') {
          searchParams.artifact_type = filterType;
        }
        
        if (filterSourceId) {
          searchParams.source_id = filterSourceId;
        }
        
        const result = await searchArtifacts(searchParams);
        setSearchResults(result.artifacts);
        setSearchResultsTotal(result.total);
        return {
          artifacts: result.artifacts,
          total: result.total,
          limit: result.limit,
          offset: result.offset,
          has_more: result.has_more,
        };
      } else {
        // Use list API for unfiltered view (first 100)
        // If we have source_id from URL but no other filters, use list API with source_id
        const result = await listArtifacts(
          projectId!, 
          pageSize, 
          currentPage * pageSize,
          filterSourceId || undefined
        );
        setSearchResults(null);
        setSearchResultsTotal(result.total || result.artifacts.length);
        return result;
      }
    },
    enabled: !!projectId,
    refetchInterval: hasActiveFilters ? false : 30000, // Don't auto-refresh when searching
  });

  const artifacts = artifactsData?.artifacts || [];

  // Fetch stats from API endpoint (aggregate values, not from loaded artifacts)
  const { data: apiStats, isLoading: loadingStats } = useQuery({
    queryKey: ['artifact-stats', projectId],
    queryFn: () => getArtifactStats(projectId!),
    enabled: !!projectId,
    refetchInterval: 60000, // Refresh stats every minute
  });

  const totalArtifactCount = hasActiveFilters
    ? (searchResultsTotal || artifactsData?.total || 0)
    : (apiStats?.total_artifacts ?? artifactsData?.total ?? artifacts.length);

  const totalPages = Math.max(1, Math.ceil(totalArtifactCount / pageSize));
  const hasMorePages = (currentPage + 1) * pageSize < totalArtifactCount;
  const pageStart = totalArtifactCount === 0 ? 0 : currentPage * pageSize + 1;
  const pageEnd = Math.min((currentPage + 1) * pageSize, totalArtifactCount);

  // Fetch chunks for expanded artifact - includes content directly!
  const { data: chunksList, isLoading: loadingChunks } = useQuery({
    queryKey: ['artifact-chunks', expandedArtifact],
    queryFn: () => getArtifactChunks(expandedArtifact!),
    enabled: !!expandedArtifact && !!projectId,
  });

  // Fetch entity counts for all artifacts (for sorting) - OPTIMIZED: batch query
  const [entityCounts, setEntityCounts] = useState<Record<string, number>>({});
  const fetchedArtifactIdsRef = React.useRef<Set<string>>(new Set());
  const fetchingRef = React.useRef<boolean>(false);
  const lastArtifactIdsRef = React.useRef<string>('');
  
  React.useEffect(() => {
    if (!artifacts || artifacts.length === 0 || !projectId) return;
    
    // Create a stable string of artifact IDs to detect changes
    const currentArtifactIds = artifacts.map(a => a.id).sort().join(',');
    
    // Only proceed if artifact IDs have actually changed
    if (currentArtifactIds === lastArtifactIdsRef.current) return;
    lastArtifactIdsRef.current = currentArtifactIds;
    
    // Get artifact IDs that we haven't fetched yet
    const artifactsToFetch = artifacts.filter(
      a => !fetchedArtifactIdsRef.current.has(a.id)
    );
    
    if (artifactsToFetch.length === 0) return;
    
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    // Mark all as fetched immediately to prevent duplicate requests
    artifactsToFetch.forEach(a => {
      fetchedArtifactIdsRef.current.add(a.id);
    });
    
    // OPTIMIZED: Fetch counts for ALL artifacts in a SINGLE batch query
    (async () => {
      try {
        const artifactIds = artifactsToFetch.map(a => a.id);
        
        // Single batch query for all artifact entity counts
        const counts = await getEntityCountsBatch(projectId!, artifactIds);
        
        // Update counts
        setEntityCounts(prev => {
          const updated = { ...prev };
          Object.entries(counts).forEach(([id, count]) => {
            updated[id] = count;
          });
          return updated;
        });
      } catch (e) {
        console.error('[KnowledgeBase] Failed to fetch entity counts batch:', e);
        // On error, set all to 0
        setEntityCounts(prev => {
          const updated = { ...prev };
          artifactsToFetch.forEach(a => {
            updated[a.id] = 0;
          });
          return updated;
        });
      } finally {
        fetchingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifacts, projectId]); // Depend on artifacts array, but use refs to prevent duplicate fetches

  // Fetch chunk counts for visible artifacts (batch query) — powers sort + per-row stats
  const fetchedChunkIdsRef = React.useRef<Set<string>>(new Set());
  const fetchingChunksRef = React.useRef<boolean>(false);
  const lastChunkArtifactIdsRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!artifacts || artifacts.length === 0 || !projectId) return;

    const currentArtifactIds = artifacts.map(a => a.id).sort().join(',');
    if (currentArtifactIds === lastChunkArtifactIdsRef.current) return;
    lastChunkArtifactIdsRef.current = currentArtifactIds;

    const artifactsToFetch = artifacts.filter(a => !fetchedChunkIdsRef.current.has(a.id));
    if (artifactsToFetch.length === 0) return;
    if (fetchingChunksRef.current) return;
    fetchingChunksRef.current = true;

    artifactsToFetch.forEach(a => fetchedChunkIdsRef.current.add(a.id));

    (async () => {
      try {
        const counts = await getChunkCountsBatch(
          projectId!,
          artifactsToFetch.map(a => a.id),
        );
        setChunkCounts(prev => ({ ...prev, ...counts }));
      } catch (e) {
        console.error('[KnowledgeBase] Failed to fetch chunk counts batch:', e);
      } finally {
        fetchingChunksRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifacts, projectId]);

  // Note: Artifacts don't have direct source_artifact_id in the schema
  // Source information comes from artifact.source_type field

  // BM25 Index Status Query
  const { data: bm25Status, refetch: refetchBM25Status } = useQuery({
    queryKey: ['bm25-status', projectId],
    queryFn: () => getBM25Status(projectId!),
    enabled: !!projectId,
    refetchInterval: 30000,
  });

  // BM25 Build Mutation
  const buildBM25Mutation = useMutation({
    mutationFn: () => buildBM25Index(projectId!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['bm25-status', projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      const jobId = result.task_id || result.job_id;
      addToast({
        type: 'success',
        title: 'BM25 index build started',
        message: jobId ? `Task ID: ${jobId.substring(0, 8)}…` : (result.message || 'Build triggered'),
      });
    },
    onError: (error: any) => {
      addToast({ type: 'error', message: `Failed to build BM25 index: ${error.message || 'Unknown error'}` });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (artifactId: string) => deleteArtifact(artifactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
      addToast({ type: 'success', message: 'Document removed from knowledge base.' });
    },
    onError: (error: any) => {
      addToast({ type: 'error', title: 'Delete failed', message: error?.message || 'Could not remove the document. Please try again.' });
    },
  });

  const reingestMutation = useMutation({
    mutationFn: (artifactId: string) => reingestArtifact(artifactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
      addToast({ type: 'success', message: 'Reingest job submitted successfully' });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadArtifact(projectId!, file, setUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
      setShowUpload(false);
      setUploadFile(null);
      setUploadProgress(0);
      addToast({ type: 'success', title: 'Upload complete', message: 'File uploaded successfully and is being processed.' });
    },
    onError: (error: any) => {
      addToast({ type: 'error', message: `Upload failed: ${error?.message || 'Unknown error'}` });
    },
  });

  // DB chunk counts from batch API (sorting fallback; display uses processing_status via helpers)
  const [chunkCounts, setChunkCounts] = useState<Record<string, number>>({});

  // Helper function to extract source name from artifact
  // Priority: meta.source_name > storage_path (cleaned) > 'Direct Uploads'
  // MUST be defined before filteredAndSortedArtifacts which uses it
  const getArtifactSourceName = React.useCallback((artifact: Artifact): string => {
    // First try meta.source_name
    if (artifact.meta?.source_name) {
      return artifact.meta.source_name;
    }
    
    // Fallback to storage_path - extract meaningful part
    if (artifact.storage_path) {
      let path = artifact.storage_path;
      
      // Remove protocol prefixes (unc://, blob://, etc.)
      path = path.replace(/^[a-z]+:\/\//i, '');
      
      // Extract first meaningful directory or use the path up to first few segments
      const parts = path.split(/[\/\\]/).filter(p => p && p.trim());
      
      if (parts.length > 0) {
        // Use first directory as source name, or first two if first is too generic
        if (parts.length === 1) {
          return parts[0];
        } else {
          // Combine first two parts for more context (e.g., "key-fs2012/Common")
          return `${parts[0]}/${parts[1]}`;
        }
      }
      
      // If path parsing fails, use the cleaned path itself
      return path || 'Direct Uploads';
    }
    
    return 'Direct Uploads';
  }, []);

  const filteredAndSortedArtifacts = React.useMemo(() => {
    // When using search API with source_id, results are already filtered by source
    // When using list API or filtering by source name, we need client-side filtering
    const sourceArtifacts = artifacts;
    if (!sourceArtifacts) return [];
    
    // If we're filtering by source_id via API, no need for client-side filtering
    // Only apply client-side filtering if filtering by source name (not source_id)
    let filtered = sourceArtifacts;
    
    // Only apply source name filter if we're NOT using source_id filter
    // (source_id filter is handled by API, source name filter is client-side)
    if (filterSourceId === null && filterSource !== 'all') {
      // Apply source name filter (client-side, since search API doesn't support source name filtering)
      filtered = sourceArtifacts.filter((artifact) => {
        const sourceName = getArtifactSourceName(artifact);
        return sourceName === filterSource;
      });
    }
    
    // Search API already handled search/type/source_id filtering when hasActiveFilters is true
    // Client-side filtering only needed for source name filtering

    filtered = applyRegistryFilters(filtered, registryFilters);

    // Sort artifacts
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortBy) {
        case 'size':
          aVal = a.file_size_bytes || 0;
          bVal = b.file_size_bytes || 0;
          break;
        case 'date':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'name':
          aVal = a.filename?.toLowerCase() || '';
          bVal = b.filename?.toLowerCase() || '';
          break;
        case 'chunks':
          aVal = getDisplayChunkCount(a, chunkCounts);
          bVal = getDisplayChunkCount(b, chunkCounts);
          break;
        case 'entities':
          aVal = getDisplayEntityCount(a, entityCounts);
          bVal = getDisplayEntityCount(b, entityCounts);
          break;
        case 'status':
          aVal = getIngestStatusLabel(a) || 'unknown';
          bVal = getIngestStatusLabel(b) || 'unknown';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [artifacts, searchTerm, filterType, filterSource, filterSourceId, registryFilters, sortBy, sortOrder, entityCounts, chunkCounts, getArtifactSourceName]);

  const groupedArtifacts = React.useMemo(
    () => groupArtifactsByRegistryField(filteredAndSortedArtifacts, groupBy, getArtifactSourceName),
    [filteredAndSortedArtifacts, groupBy, getArtifactSourceName],
  );

  const registryFilterOptions = React.useMemo(
    () => getRegistryFilterOptions(artifacts),
    [artifacts],
  );

  const registryArtifactCount = React.useMemo(
    () => artifacts.filter(hasRegistryMeta).length,
    [artifacts],
  );

  // Get unique artifact types for filter
  const artifactTypes = React.useMemo(() => {
    if (!artifacts) return [];
    return Array.from(new Set(artifacts.map(a => a.artifact_type)));
  }, [artifacts]);

  // Get unique source names for filter - prioritize from sources API, fallback to artifacts
  const sourceNames = React.useMemo(() => {
    // First, try to get from sources API
    if (sources && sources.length > 0) {
      const apiSources = sources
        .map(s => s.name || s.description)
        .filter((name): name is string => !!name);
      
      // Also include source names from artifacts (storage paths)
      const artifactSources = new Set<string>();
      if (artifacts) {
        artifacts.forEach(a => {
          const sourceName = getArtifactSourceName(a);
          artifactSources.add(sourceName);
        });
      }
      
      // Combine and deduplicate
      const allSources = new Set([...apiSources, ...Array.from(artifactSources)]);
      return Array.from(allSources).sort();
    }
    
    // Fallback: extract from artifacts using storage_path
    if (!artifacts) return [];
    const sourceSet = new Set<string>();
    artifacts.forEach(a => {
      const sourceName = getArtifactSourceName(a);
      sourceSet.add(sourceName);
    });
    return Array.from(sourceSet).sort();
  }, [artifacts, sources, getArtifactSourceName]);

  // Markdown to HTML converter (for rendering chunk content)
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';
    let html = markdown;

    // Escape HTML first
    html = html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Code blocks
    html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Markdown tables (basic support)
    const tableRegex = /(\|.+\|(?:\n\|[:\-\s\|]+\|)?(?:\n\|.+\|)+)/g;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n').filter(l => l.trim());
      if (lines.length < 2) return match;
      
      const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
      const rows = lines.slice(2).map(line => 
        line.split('|').map(c => c.trim()).filter((c, i) => i > 0 && i <= headers.length)
      );
      
      let tableHtml = '<table class="min-w-full divide-y divide-gray-200 border border-gray-300 text-sm"><thead class="bg-gray-100"><tr>';
      headers.forEach(header => {
        tableHtml += `<th class="px-3 py-2 text-left font-medium text-gray-700 border-r border-gray-300">${header}</th>`;
      });
      tableHtml += '</tr></thead><tbody class="bg-white divide-y divide-gray-200">';
      rows.forEach(row => {
        tableHtml += '<tr>';
        row.forEach(cell => {
          tableHtml += `<td class="px-3 py-2 text-theme-text border-r border-theme-border">${cell || '-'}</td>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</tbody></table>';
      return tableHtml;
    });

    // Lists
    html = html.replace(/^\- (.*$)/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul class="list-disc list-inside">$1</ul>');

    // Paragraphs
    html = html.split('\n\n').map((para) => {
      if (!para.trim().startsWith('<') && para.trim()) {
        return `<p class="mb-2">${para}</p>`;
      }
      return para;
    }).join('\n');

    return html;
  };

  // Calculate stats from API stats endpoint (project-wide aggregates)
  const stats = React.useMemo(() => {
    const filtered = filteredAndSortedArtifacts;

    const total = apiStats?.total_artifacts ?? filtered.length;
    const totalSizeMB = apiStats?.total_size_mb
      ? apiStats.total_size_mb.toFixed(2)
      : (filtered.reduce((sum, a) => sum + (a.file_size_bytes || 0), 0) / 1024 / 1024).toFixed(2);
    const byType = apiStats?.by_type || filtered.reduce((acc, a) => {
      acc[a.artifact_type] = (acc[a.artifact_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const bySource: Record<string, number> = {};
    filtered.forEach(a => {
      const sourceName = getArtifactSourceName(a);
      bySource[sourceName] = (bySource[sourceName] || 0) + 1;
    });

    const statsStale = isArtifactStatsStale(apiStats);
    const artifactsWithChunks = statsStale ? undefined : (apiStats?.artifacts_with_chunks ?? 0);
    const totalChunks = statsStale ? undefined : (apiStats?.total_chunks ?? 0);
    const indexedChunkEntries = statsStale ? undefined : (apiStats?.indexed_chunk_entries ?? 0);
    const byIngestStatus = apiStats?.by_ingest_status ?? {};
    const bm25ChunkCount = bm25Status?.total_chunks ?? bm25Status?.total_documents ?? 0;

    return {
      statsStale,
      total,
      totalSizeMB,
      byType,
      bySource,
      sourceCount: Object.keys(bySource).length,
      artifactsWithChunks,
      artifactsWithoutChunks: statsStale
        ? undefined
        : (apiStats?.artifacts_without_chunks ?? Math.max(0, total - (artifactsWithChunks ?? 0))),
      totalChunks,
      indexedChunkEntries,
      byIngestStatus,
      bm25ChunkCount,
      loadedCount: filtered.length,
    };
  }, [apiStats, filteredAndSortedArtifacts, getArtifactSourceName, bm25Status]);

  if (!projectId) {
    return (
      <EmptyState
        icon={<Database className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to view knowledge base"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-theme-text">Knowledge Base</h1>
            <p className="text-theme-text-secondary mt-1">
              View artifacts, chunks, and document coverage
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1 bg-theme-surface-hover rounded-lg p-1 border border-theme-border">
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
            >
              <List className="h-4 w-4 mr-2" />
              List
            </Button>
            <Button
              onClick={() => setViewMode('tree')}
              variant={viewMode === 'tree' ? 'primary' : 'ghost'}
              size="sm"
            >
              <FolderTree className="h-4 w-4 mr-2" />
              Tree
            </Button>
            <Button
              onClick={() => setViewMode('gaps')}
              variant={viewMode === 'gaps' ? 'primary' : 'ghost'}
              size="sm"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Gaps
            </Button>
          </div>
          <select
            value={sortByDate}
            onChange={(e) => setSortByDate(e.target.value as 'recent_upload' | 'recent_indexed')}
            className="px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text text-sm dark:text-black"
            title={sortByDate === 'recent_indexed' ? 'Currently sorted by upload date (indexed date sorting coming soon)' : 'Sorted by upload date'}
          >
            <option value="recent_upload">Most Recently Uploaded</option>
            <option value="recent_indexed">Most Recently Indexed</option>
          </select>
          <Button
            onClick={() => refetch()}
            variant="outline"
            disabled={isRefetching}
            aria-busy={isRefetching}
            title={isRefetching ? 'Refreshing knowledge base…' : 'Reload artifacts from server'}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 shrink-0 ${isRefetching ? 'animate-spin text-primary-600' : ''}`}
              aria-hidden
            />
            {isRefetching ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (viewMode === 'list' || viewMode === 'tree') && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-blue-700 font-semibold">Documents</div>
                  <div className="text-3xl font-bold text-blue-900">
                    {loadingStats ? '...' : stats.total.toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} on this page
                  </div>
                </div>
                <FileText className="h-10 w-10 text-blue-500" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-emerald-700 font-semibold">Chunked Documents</div>
                  <div className="text-3xl font-bold text-emerald-900">
                    {loadingStats ? '...' : stats.statsStale ? '—' : (stats.artifactsWithChunks ?? 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-emerald-600 mt-1">
                    {stats.statsStale ? (
                      'Restart API to load chunk stats'
                    ) : stats.total > 0 ? (
                      <>
                        {`${(((stats.artifactsWithChunks ?? 0) / stats.total) * 100).toFixed(1)}% of documents`}
                        {(stats.artifactsWithoutChunks ?? 0) > 0
                          ? ` · ${(stats.artifactsWithoutChunks ?? 0).toLocaleString()} unchunked`
                          : ''}
                      </>
                    ) : (
                      '0%'
                    )}
                  </div>
                </div>
                <Database className="h-10 w-10 text-emerald-500" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-amber-700 font-semibold">Indexed Chunks</div>
                  <div className="text-3xl font-bold text-amber-900">
                    {loadingStats ? '...' : stats.statsStale ? '—' : (stats.indexedChunkEntries ?? 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">
                    {stats.statsStale
                      ? 'Restart API to load index stats'
                      : (stats.totalChunks ?? 0) > 0
                      ? `${(stats.totalChunks ?? 0).toLocaleString()} evidence chunks total`
                      : 'BM25 + vector index rows'}
                  </div>
                </div>
                <Zap className="h-10 w-10 text-amber-500" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-purple-700 font-semibold">Total Size</div>
                  <div className="text-3xl font-bold text-purple-900">{stats.totalSizeMB} MB</div>
                </div>
                <Database className="h-10 w-10 text-purple-500" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-green-700 font-semibold">Document Types</div>
                  <div className="text-3xl font-bold text-green-900">
                    {loadingStats ? '...' : Object.keys(stats.byType).length}
                  </div>
                </div>
                <FileCheck className="h-10 w-10 text-green-500" />
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-indigo-700 font-semibold">Ingestion Status</div>
                  <div className="flex flex-wrap gap-2 mt-2 text-sm">
                    {Object.entries(stats.byIngestStatus).length > 0 ? (
                      Object.entries(stats.byIngestStatus).map(([status, count]) => (
                        <Badge key={status} variant="default" className="bg-white/80 text-indigo-900">
                          {status}: {count.toLocaleString()}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-indigo-600 text-sm">No ingest status data</span>
                    )}
                  </div>
                </div>
                <Network className="h-10 w-10 text-indigo-500" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-orange-700 font-semibold">Avg Document Size</div>
                  <div className="text-3xl font-bold text-orange-900">
                    {stats.total > 0 ? (parseFloat(stats.totalSizeMB) / stats.total).toFixed(1) : 0} MB
                  </div>
                  <div className="text-xs text-orange-600 mt-1">
                    {stats.totalSizeMB} MB total · {stats.sourceCount} sources on page
                  </div>
                </div>
                <BarChart3 className="h-10 w-10 text-orange-500" />
              </div>
            </Card>
          </div>
        </>
      )}

      {/* BM25 Index Status Card */}
      {(viewMode === 'list' || viewMode === 'tree') && (
        <Card className={`border-2 ${
          bm25Status?.build_status === 'complete' 
            ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' 
            : bm25Status?.build_status === 'building'
            ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50'
            : 'border-gray-300 bg-gradient-to-r from-gray-50 to-slate-50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                bm25Status?.build_status === 'complete' 
                  ? 'bg-green-100' 
                  : bm25Status?.build_status === 'building'
                  ? 'bg-yellow-100'
                  : 'bg-gray-100'
              }`}>
                <Zap className={`h-8 w-8 ${
                  bm25Status?.build_status === 'complete' 
                    ? 'text-green-600' 
                    : bm25Status?.build_status === 'building'
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-theme-text dark:text-black">BM25 Keyword Index</h3>
                  {bm25Status?.build_status === 'complete' && (
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ready
                    </Badge>
                  )}
                  {bm25Status?.build_status === 'building' && (
                    <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Building
                    </Badge>
                  )}
                  {bm25Status?.build_status === 'not_built' && (
                    <Badge variant="default" className="bg-gray-100 text-gray-800">
                      Not Built
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Enables hybrid search (vector + keyword) for better retrieval
                </p>
                {bm25Status && (
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{(bm25Status.total_chunks ?? bm25Status.total_documents).toLocaleString()} BM25 chunks indexed</span>
                    <span>
                      {stats.statsStale
                        ? '— documents chunked'
                        : `${(stats.artifactsWithChunks ?? 0).toLocaleString()} documents chunked`}
                    </span>
                    <span>{bm25Status.index_size_mb.toFixed(2)} MB</span>
                    {bm25Status.last_built_at && (
                      <span>Last built: {new Date(bm25Status.last_built_at).toLocaleDateString()}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchBM25Status()}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => buildBM25Mutation.mutate()}
                disabled={buildBM25Mutation.isPending || bm25Status?.build_status === 'building'}
                size="sm"
              >
                {buildBM25Mutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Building...
                  </>
                ) : bm25Status?.build_status === 'complete' ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Rebuild Index
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-1" />
                    Build Index
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* View Modes */}
      {viewMode === 'gaps' ? (
        <DocumentGapsPanel 
          projectId={projectId} 
          onUploadClick={(docType) => {
            console.log('Upload requested for:', docType);
            setShowUpload(true);
          }}
        />
      ) : (
        <>
          {/* Filters */}
          <Card>
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Input
                  type="text"
                  placeholder="Search artifacts (searches filename, path, metadata)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    // Reset to first page on search
                    setCurrentPage(0);
                  }}
                  leftIcon={<Search className="h-4 w-4 text-gray-400 mr-2" />}
                  className="dark:text-black"
                />
                {hasActiveFilters && (
                  <div className="text-xs text-theme-text-secondary mt-1">
                    Using server-side search (showing {artifacts.length} of {searchResultsTotal || artifactsData?.total || 0} results)
                  </div>
                )}
              </div>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(0);
                }}
                className="px-4 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
              >
                <option value="all">All Types</option>
                {artifactTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={filterSource}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setFilterSource(selectedValue);
                  setCurrentPage(0); // Reset to first page on filter change
                  
                  // If selecting a source by name, find its ID
                  if (selectedValue !== 'all' && sources) {
                    const source = sources.find(s => 
                      (s.name || s.description || s.id) === selectedValue
                    );
                    if (source) {
                      setFilterSourceId(source.id);
                      // Update URL
                      const url = new URL(window.location.href);
                      url.searchParams.set('source_id', source.id);
                      window.history.pushState({}, '', url.toString());
                    }
                  } else {
                    setFilterSourceId(null);
                    // Remove source_id from URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('source_id');
                    window.history.pushState({}, '', url.toString());
                  }
                }}
                className="px-4 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
              >
                <option value="all">All Sources</option>
                {sources?.map((source) => (
                  <option key={source.id} value={source.name || source.description || source.id}>
                    {source.name || source.description || source.id}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="px-4 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
                title="Sort current page"
              >
                <option value="date">Sort: Date</option>
                <option value="name">Sort: Name</option>
                <option value="size">Sort: Size</option>
                <option value="chunks">Sort: Chunks</option>
                <option value="entities">Sort: Entities</option>
                <option value="status">Sort: Ingest Status</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </Button>
              <Button
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                variant={showAdvancedSearch ? 'primary' : 'outline'}
                size="sm"
              >
                <Filter className="h-4 w-4 mr-2" />
                Advanced Search
                {searchResults !== null && (
                  <Badge variant="default" className="ml-2">
                    {searchResultsTotal} results
                  </Badge>
                )}
              </Button>
              {searchResults !== null && (
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchResults(null);
                    setSearchResultsTotal(0);
                  }}
                  variant="outline"
                  size="sm"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Search
                </Button>
              )}
            </div>

            <RegistryFilterBar
              filters={registryFilters}
              options={registryFilterOptions}
              groupBy={groupBy}
              registryArtifactCount={registryArtifactCount}
              onFiltersChange={(next) => {
                setRegistryFilters(next);
                setCurrentPage(0);
              }}
              onGroupByChange={setGroupBy}
              onClear={() => setRegistryFilters(DEFAULT_REGISTRY_FILTERS)}
            />
          </Card>

          {/* Advanced Search Panel */}
          {showAdvancedSearch && (
            <AdvancedArtifactSearch
              projectId={projectId!}
              onResults={(results, total) => {
                setSearchResults(results);
                setSearchResultsTotal(total);
                setShowAdvancedSearch(false);
              }}
              onClose={() => setShowAdvancedSearch(false)}
              artifactTypes={artifactTypes}
              sources={sources || []}
            />
          )}

          {/* Tree View */}
          {viewMode === 'tree' && (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : filteredAndSortedArtifacts.length === 0 ? (
                <EmptyState
                  icon={<FolderTree className="h-12 w-12" />}
                  title="No Artifacts Found"
                  description="Upload documents to build your knowledge base"
                  action={
                    <Button onClick={() => setShowUpload(true)}>
                      <Upload className="h-4 w-4" />
                      Upload First Document
                    </Button>
                  }
                />
              ) : (
                <FileTree
                  root={buildFileTree(filteredAndSortedArtifacts)}
                  expandedPaths={expandedTreePaths}
                  onTogglePath={(path) => {
                    setExpandedTreePaths(prev => {
                      const next = new Set(prev);
                      if (next.has(path)) {
                        next.delete(path);
                      } else {
                        next.add(path);
                      }
                      return next;
                    });
                  }}
                  onArtifactClick={(artifact) => {
                    setExpandedArtifact(expandedArtifact === artifact.id ? null : artifact.id);
                  }}
                  selectedArtifactId={expandedArtifact}
                  searchTerm={searchTerm}
                />
              )}
            </>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : filteredAndSortedArtifacts.length === 0 ? (
                <EmptyState
                  icon={<FileText className="h-12 w-12" />}
                  title="No Artifacts Found"
                  description="Upload documents to build your knowledge base"
                  action={
                    <Button onClick={() => setShowUpload(true)}>
                      <Upload className="h-4 w-4" />
                      Upload First Document
                    </Button>
                  }
                />
              ) : (
                <div className="space-y-3">
                  {/* Pagination summary */}
                  {totalArtifactCount > pageSize && (
                    <Card className="bg-slate-50 border-slate-200">
                      <div className="flex items-center justify-between p-3">
                        <div className="text-sm text-slate-700">
                          Showing {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of {totalArtifactCount.toLocaleString()} documents
                          {hasActiveFilters ? ' (filtered)' : ''}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            Page {currentPage + 1} of {totalPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                            disabled={currentPage === 0}
                          >
                            Previous
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={!hasMorePages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Results Summary (search mode) */}
                  {hasActiveFilters && totalArtifactCount <= pageSize && (
                    <Card className="bg-blue-50 border-blue-200">
                      <div className="flex items-center justify-between p-3">
                        <div className="text-sm text-blue-900">
                          <strong>Server-side search active:</strong> Showing {artifacts.length} of {searchResultsTotal || artifactsData?.total || 0} results
                          {artifactsData?.has_more && (
                            <span className="ml-2 text-blue-600">(more available)</span>
                          )}
                        </div>
                        {artifactsData?.has_more && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                              disabled={currentPage === 0}
                            >
                              Previous
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCurrentPage(prev => prev + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                  
                  {Object.entries(groupedArtifacts).map(([groupName, sourceArtifacts]) => {
                    const showSourceHeader = groupBy !== 'none' && groupName !== 'All Artifacts';
                    const isSourceExpanded = expandedSources.has(groupName);
                    const groupDisplayName =
                      groupBy === 'project_id'
                        ? getProjectGroupDisplayLabel(sourceArtifacts, groupName)
                        : groupName;

                    return (
                      <div key={groupName}>
                        {/* Group header (when grouping enabled) */}
                        {showSourceHeader && (
                          <Card className="mb-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                            <div
                              className="flex items-center justify-between cursor-pointer p-4"
                              onClick={() => {
                                setExpandedSources(prev => {
                                  const next = new Set(prev);
                                  if (next.has(groupName)) {
                                    next.delete(groupName);
                                  } else {
                                    next.add(groupName);
                                  }
                                  return next;
                                });
                              }}
                            >
                              <div className="flex items-center gap-3">
                                {isSourceExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-blue-600" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-blue-600" />
                                )}
                                <Layers className="h-5 w-5 text-blue-600" />
                                <div>
                                  <h3 className="font-semibold text-blue-900">{groupDisplayName}</h3>
                                  <p className="text-sm text-blue-700">
                                    {sourceArtifacts.length} {sourceArtifacts.length === 1 ? 'artifact' : 'artifacts'}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="default" className="bg-blue-100 text-blue-800">
                                {sourceArtifacts.length}
                              </Badge>
                            </div>
                          </Card>
                        )}

                        {/* Artifacts in this source */}
                        {(showSourceHeader ? isSourceExpanded : true) && (
                          <div className={showSourceHeader ? "ml-4 space-y-3" : "space-y-3"}>
                            {sourceArtifacts.map((artifact) => {
                const isExpanded = expandedArtifact === artifact.id;
                const sizeMB = ((artifact.file_size_bytes || 0) / 1024 / 1024).toFixed(2);
                
                return (
                  <Card key={artifact.id} className="hover:shadow-lg transition-all">
                    <div className="space-y-4">
                      {/* Artifact Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              onClick={() => setExpandedArtifact(isExpanded ? null : artifact.id)}
                              className="text-gray-600 hover:text-gray-900 transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5" />
                              ) : (
                                <ChevronRight className="h-5 w-5" />
                              )}
                            </button>
                            <FileText className="h-6 w-6 text-blue-600" />
                            <div className="flex-1">
                              <h3 className="font-semibold text-theme-text text-lg">{artifact.filename}</h3>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <Badge>{artifact.artifact_type}</Badge>
                                <span className="text-sm text-gray-500">{sizeMB} MB</span>
                                <span className="text-sm text-gray-500">
                                  {new Date(artifact.created_at).toLocaleDateString()}
                                </span>
                                <span className="text-sm font-medium text-purple-600">
                                  {getDisplayChunkCount(artifact, chunkCounts)}{' '}
                                  {getDisplayChunkCount(artifact, chunkCounts) === 1 ? 'chunk' : 'chunks'}
                                </span>
                                <span className="text-sm font-medium text-green-600">
                                  {getDisplayEntityCount(artifact, entityCounts)}{' '}
                                  {getDisplayEntityCount(artifact, entityCounts) === 1 ? 'entity' : 'entities'}
                                </span>
                                {getIngestStatusLabel(artifact) && (
                                  <Badge
                                    variant="default"
                                    className={`text-xs ${getIngestStatusBadgeClass(getIngestStatusLabel(artifact))}`}
                                  >
                                    {getIngestStatusLabel(artifact)}
                                  </Badge>
                                )}
                              </div>
                              <RegistryMetaBadges artifact={artifact} compact />
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadArtifactWithToast(artifact.id, artifact.filename, addToast)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reingestMutation.mutate(artifact.id)}
                            disabled={reingestMutation.isPending}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (window.confirm(`Delete ${artifact.filename}? This will also delete all associated chunks.`)) {
                                deleteMutation.mutate(artifact.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <RelatedRegistryArtifacts
                          artifact={artifact}
                          allArtifacts={artifacts}
                          onSelectArtifact={(artifactId) => setExpandedArtifact(artifactId)}
                        />
                      )}

                      {/* Artifact Details - Only shown when expanded */}
                      {isExpanded && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">Chunks</div>
                              <div className="font-medium text-primary-600">
                                {getDisplayChunkCount(artifact, chunkCounts)}
                              </div>
                            </div>
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">Entities</div>
                              <div className="font-medium text-green-600">
                                {getDisplayEntityCount(artifact, entityCounts)}
                              </div>
                            </div>
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">SHA256</div>
                              <div className="truncate font-mono text-xs text-theme-text">
                                {artifact.sha256 ? artifact.sha256.substring(0, 16) + '...' : 'N/A'}
                              </div>
                            </div>
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">Source Type</div>
                              <div className="font-medium text-theme-text">{artifact.source_type || 'upload'}</div>
                            </div>
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">Storage Path</div>
                              <div className="truncate font-mono text-xs text-theme-text">
                                {artifact.storage_path?.startsWith('unc://') ? (
                                  <span className="text-blue-600" title={artifact.storage_path}>
                                    {artifact.storage_path.replace(/^unc:\/\//i, '\\\\').replace(/\//g, '\\')}
                                  </span>
                                ) : (
                                  artifact.storage_path || 'N/A'
                                )}
                              </div>
                            </div>
                            <div className="rounded bg-gray-50 px-3 py-2">
                              <div className="mb-0.5 text-xs text-gray-500">ID</div>
                              <div className="truncate font-mono text-xs text-theme-text">
                                {artifact.id ? artifact.id.substring(0, 12) + '...' : 'N/A'}
                              </div>
                            </div>
                          </div>
                          <RegistryMetaDetailGrid artifact={artifact} />
                        </div>
                      )}
                      
                      {/* Source Information Card */}
                      {artifact.meta?.source_name && (
                        <div className="mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Layers className="h-4 w-4 text-blue-600" />
                            <div className="text-blue-900 font-semibold text-sm">Source: {artifact.meta.source_name}</div>
                            {artifact.meta.is_reference && (
                              <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                                📍 Reference
                              </Badge>
                            )}
                          </div>
                          {artifact.meta.relative_path && (
                            <div className="text-blue-700 text-xs font-mono truncate" title={artifact.meta.relative_path}>
                              📂 {artifact.meta.relative_path}
                            </div>
                          )}
                          {artifact.meta.source_root_path && (
                            <div className="text-blue-600 text-xs mt-1 font-mono truncate" title={artifact.meta.source_root_path}>
                              Root: {artifact.meta.source_root_path}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Processing Status */}
                      {artifact.meta?.processing_status && (
                        <div className={`mt-3 p-3 rounded-lg border ${
                          artifact.meta.processing_status.status === 'completed' 
                            ? 'bg-green-50 border-green-200' 
                            : artifact.meta.processing_status.status === 'partial'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            {artifact.meta.processing_status.status === 'completed' ? (
                              <FileCheck className="h-4 w-4 text-green-600" />
                            ) : artifact.meta.processing_status.status === 'partial' ? (
                              <AlertCircle className="h-4 w-4 text-yellow-600" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-600" />
                            )}
                            <h4 className="font-semibold text-sm">
                              Processing Status: {artifact.meta.processing_status.status.toUpperCase()}
                            </h4>
                          </div>
                          <div className="text-xs space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Parse:</span>
                              <Badge variant={artifact.meta.processing_status.parse_success ? 'default' : 'error'}>
                                {artifact.meta.processing_status.parse_success ? 'Success' : 'Failed'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Chunks:</span>
                              <Badge variant={artifact.meta.processing_status.chunk_success ? 'default' : 'error'}>
                                {artifact.meta.processing_status.chunks_created} created
                              </Badge>
                            </div>
                            {artifact.meta.processing_status.entity_extraction_success !== null && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium">Entities:</span>
                                <Badge variant={artifact.meta.processing_status.entity_extraction_success ? 'default' : 'error'}>
                                  {artifact.meta.processing_status.entities_created} extracted
                                </Badge>
                              </div>
                            )}
                            {artifact.meta.processing_status.error && (
                              <div className="text-red-700 mt-2">
                                <span className="font-medium">Error:</span> {artifact.meta.processing_status.error}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Source Information - Show when expanded */}
                      {isExpanded && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Layers className="h-4 w-4 text-blue-600" />
                            <h4 className="font-semibold text-blue-900">Source Information</h4>
                          </div>
                          <div className="text-sm text-blue-700 space-y-2">
                            <div>
                              <span className="font-medium">Source Type:</span> {artifact.source_type || 'Direct Upload'}
                            </div>
                            
                            {/* API Integration Details */}
                            {artifact.source_type?.startsWith('api_integration_') && artifact.meta && (
                              <div className="mt-3 pt-3 border-t border-blue-300">
                                <div className="font-semibold mb-2 text-blue-900">API Integration Details</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {artifact.meta.integration_display_name && (
                                    <div>
                                      <span className="font-medium">Integration:</span>{' '}
                                      <span className="text-blue-800">{artifact.meta.integration_display_name}</span>
                                    </div>
                                  )}
                                  {artifact.meta.item_count !== undefined && (
                                    <div>
                                      <span className="font-medium">Items:</span>{' '}
                                      <span className="text-blue-800">{artifact.meta.item_count.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {artifact.meta.source_name && (
                                    <div>
                                      <span className="font-medium">Source Name:</span>{' '}
                                      <span className="text-blue-800">{artifact.meta.source_name}</span>
                                    </div>
                                  )}
                                  {artifact.meta.fetched_at && (
                                    <div>
                                      <span className="font-medium">Fetched:</span>{' '}
                                      <span className="text-blue-800">
                                        {new Date(artifact.meta.fetched_at).toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  {artifact.meta.sync_job_id && (
                                    <div className="col-span-2">
                                      <span className="font-medium">Sync Job ID:</span>{' '}
                                      <span className="font-mono text-blue-800 text-[10px]">
                                        {artifact.meta.sync_job_id.substring(0, 8)}...
                                      </span>
                                    </div>
                                  )}
                                  {artifact.meta.filters_applied && Object.keys(artifact.meta.filters_applied).length > 0 && (
                                    <div className="col-span-2 mt-2">
                                      <span className="font-medium">Filters Applied:</span>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {Object.entries(artifact.meta.filters_applied).map(([key, value]) => (
                                          <Badge key={key} variant="default" size="sm" className="text-xs">
                                            {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Show opportunity preview if available */}
                                {artifact.meta.item_count > 0 && (
                                  <div className="mt-3 pt-3 border-t border-blue-300">
                                    <div className="text-xs text-blue-600">
                                      This artifact contains {artifact.meta.item_count} opportunity/award records from{' '}
                                      {artifact.meta.integration_display_name || artifact.meta.integration_type}.
                                      {artifact.meta.item_count > 100 && (
                                        <span className="block mt-1 text-blue-500">
                                          💡 Tip: Expand chunks below to see individual opportunity details
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expanded: Show Chunks */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Database className="h-5 w-5 text-purple-600" />
                            <h4 className="font-semibold text-theme-text">
                              Evidence Chunks ({chunksList?.total_chunks ?? chunksList?.chunks?.length ?? 0} total)
                            </h4>
                          </div>
                          
                          {loadingChunks ? (
                            <div className="flex items-center justify-center py-8">
                              <LoadingSpinner size="md" />
                              <span className="ml-2 text-gray-600">Loading chunks...</span>
                            </div>
                          ) : chunksList && chunksList.chunks && chunksList.chunks.length > 0 ? (
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                              {chunksList.chunks.map((chunk, idx) => {
                                const hasTable = chunk.meta?.contains_table === true;
                                const hasFigure = chunk.meta?.contains_figure === true;
                                const chunkKey = chunk.id || `chunk-${idx}`;
                                const isChunkExpanded = expandedChunks.has(chunkKey);
                                
                                return (
                                  <div
                                    key={chunk.id || idx}
                                    className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded">
                                          #{chunk.position !== undefined ? chunk.position + 1 : idx + 1}
                                        </span>
                                        <Badge variant="default">{chunk.chunk_strategy || 'unknown'}</Badge>
                                        {hasTable && (
                                          <Badge variant="default" className="bg-blue-100 text-blue-800">
                                            <Table className="h-3 w-3 inline mr-1" />
                                            Table
                                          </Badge>
                                        )}
                                        {hasFigure && (
                                          <Badge variant="default" className="bg-green-100 text-green-800">
                                            Figure
                                          </Badge>
                                        )}
                                        {chunk.summaries?.table_description && (
                                          <Badge variant="default" className="bg-purple-100 text-purple-800">
                                            📊 Table Description
                                          </Badge>
                                        )}
                                        {chunk.summaries?.long_chunk_summary && (
                                          <Badge variant="default" className="bg-orange-100 text-orange-800">
                                            📝 Summary
                                          </Badge>
                                        )}
                                        {chunk.meta?.page_number && (
                                          <span className="text-xs text-gray-600">Page {chunk.meta.page_number}</span>
                                        )}
                                      </div>
                                      <span className="text-xs text-gray-500 font-mono">
                                        {chunk.id ? chunk.id.substring(0, 8) : 'N/A'}
                                      </span>
                                    </div>
                                    {/* Show summaries if available */}
                                    {(chunk.summaries?.table_description || chunk.summaries?.long_chunk_summary) && (
                                      <div className="mb-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                                        <div className="text-xs font-semibold text-indigo-900 mb-2">📋 Summaries</div>
                                        {chunk.summaries.table_description && (
                                          <div className="mb-2">
                                            <div className="text-xs font-medium text-indigo-700 mb-1">Table Description:</div>
                                            <div className="text-sm text-indigo-800">{chunk.summaries.table_description}</div>
                                          </div>
                                        )}
                                        {chunk.summaries.long_chunk_summary && (
                                          <div>
                                            <div className="text-xs font-medium text-indigo-700 mb-1">Chunk Summary:</div>
                                            <div className="text-sm text-indigo-800">{chunk.summaries.long_chunk_summary}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    <div className="text-sm text-gray-700 bg-white rounded border border-purple-100">
                                      {chunk.content ? (
                                        <>
                                          {!isChunkExpanded && (
                                            <div className="p-2 border-b border-purple-100">
                                              <button
                                                onClick={() => setExpandedChunks(prev => new Set(prev).add(chunkKey))}
                                                className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                                              >
                                                <ChevronRight className="h-3 w-3" />
                                                Click to expand chunk content ({chunk.content.length} chars)
                                              </button>
                                            </div>
                                          )}
                                          {isChunkExpanded && (
                                            <div className="p-4">
                                              <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs text-gray-500">Chunk Content</span>
                                                <button
                                                  onClick={() => setExpandedChunks(prev => {
                                                    const next = new Set(prev);
                                                    next.delete(chunkKey);
                                                    return next;
                                                  })}
                                                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                                                >
                                                  <ChevronDown className="h-3 w-3" />
                                                  Collapse
                                                </button>
                                              </div>
                                        <div className="prose prose-slate max-w-none">
                                          <div 
                                            dangerouslySetInnerHTML={{ 
                                              __html: convertMarkdownToHTML(chunk.content) 
                                            }}
                                            className="markdown-content"
                                          />
                                        </div>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="text-center py-4">
                                          <AlertCircle className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
                                          <p className="text-gray-500 text-sm">No content available</p>
                                          <p className="text-xs text-gray-400 mt-1">Hash: {chunk.content_hash?.substring(0, 16)}...</p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                      {chunk.content_hash && (
                                        <span>Hash: {chunk.content_hash.substring(0, 16)}...</span>
                                      )}
                                      {chunk.created_at && (
                                        <span>{new Date(chunk.created_at).toLocaleDateString()}</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p>No chunks found for this artifact</p>
                              <Button
                                size="sm"
                                className="mt-3"
                                onClick={() => reingestMutation.mutate(artifact.id)}
                              >
                                Reingest to Create Chunks
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Upload Modal — opaque surface so content stays readable over the dashboard (theme vars can be translucent) */}
      {showUpload && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-artifact-title"
          className="fixed inset-0 z-[100] -top-[30px] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setShowUpload(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 text-slate-900 shadow-2xl dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4 dark:border-slate-600">
              <h3 id="upload-artifact-title" className="text-lg font-semibold">
                Upload Artifacts
              </h3>
              <button
                type="button"
                onClick={() => setShowUpload(false)}
                className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <label
                  htmlFor="knowledge-upload-file"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Select File
                </label>
                <input
                  id="knowledge-upload-file"
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="block w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 file:mr-4 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:file:bg-primary-500 dark:hover:file:bg-primary-600"
                />
              </div>

              {uploadProgress > 0 && (
                <div>
                  <div className="mb-2 text-sm text-slate-600 dark:text-slate-400">
                    Upload Progress: {uploadProgress}%
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700">
                    <div
                      className="h-2 rounded-full bg-primary-600 transition-all dark:bg-primary-500"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
                <Button variant="outline" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (uploadFile) {
                      uploadMutation.mutate(uploadFile);
                    }
                  }}
                  disabled={!uploadFile || uploadMutation.isPending}
                >
                  <Database className="mr-2 h-4 w-4" />
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

